import { CalendarViewDay } from "@mui/icons-material";
import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Breadcrumb,
  Dropdown,
  Badge,
} from "react-bootstrap";

import { RiHome6Line } from "react-icons/ri";
import {
  BsBuilding,
  BsBarChartSteps,
  BsPersonWorkspace,
  BsCurrencyDollar,
} from "react-icons/bs";


import { MdKeyboardArrowRight } from "react-icons/md";
import { Link } from "react-router-dom";

const barStyle = {
  display: "flex",
  alignItems: "flex-end",
  gap: "6px",
  height: "50px",
};

/**
 * Dashboard Component
 * Updates:
 * - Replaced Image with Inline SVG for Diamond (Ruby) icon
 * - Fixed placement and sizing to match screenshot exactly
 */

// Diamond (Ruby) SVG Component

const Superdashboard = () => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [activeOption, setActiveOption] = useState("Today");
  const [selectedRange, setSelectedRange] = useState("03/02/2026 - 03/02/2026");
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 768,
  );
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    const handleWindowResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", handleWindowResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  const dateOptions = [
    "Today",
    "Yesterday",
    "Last 7 Days",
    "Last 30 Days",
    "This Year",
    "Next Year",
    "Custom Range",
  ];

  const formatDate = (date) => {
    const d = new Date(date);
    let month = "" + (d.getMonth() + 1);
    let day = "" + d.getDate();
    const year = d.getFullYear();
    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;
    return [month, day, year].join("/");
  };
  const renderBars = (data, color) =>
    data.map((value, index) => (
      <div
        key={index}
        style={{
          width: "6px",
          height: `${value * 4}px`,
          backgroundColor: color,
          borderRadius: "3px",
        }}
      />
    ));
  const handleDateSelect = (option) => {
    const referenceDate = new Date("2026-03-02T00:00:00");
    let start = new Date(referenceDate);
    let end = new Date(referenceDate);

    switch (option) {
      case "Today":
        break;
      case "Yesterday":
        start.setDate(referenceDate.getDate() - 1);
        end.setDate(referenceDate.getDate() - 1);
        break;
      case "Last 7 Days":
        start.setDate(referenceDate.getDate() - 7);
        break;
      case "Last 30 Days":
        start.setDate(referenceDate.getDate() - 30);
        break;
      case "This Year":
        start = new Date("2026-01-01");
        end = new Date("2026-12-31");
        break;
      case "Next Year":
        start = new Date("2027-01-01");
        end = new Date("2027-12-31");
        break;
      default:
        break;
    }

    setActiveOption(option);
    setSelectedRange(`${formatDate(start)} - ${formatDate(end)}`);
    setShowDatePicker(false);
  };

  const styles = {
    wrapper: {
      backgroundColor: "#f8f9fa",
      // minHeight: "100vh",
      padding: isMobile ? "1px" : "px",
      fontFamily: "Inter, system-ui, sans-serif",
    },
    headerRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "25px",
      flexWrap: "wrap",
      gap: "12px",
    },
    headerRowMobile: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: "20px",
      gap: "12px",
      width: "100%",
    },
    title: {
      fontSize: "24px",
      fontWeight: "700",
      color: "#2d3748",
      margin: 0,
    },
    datePickerContainer: {
      position: "relative",
    },
    dateInput: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      backgroundColor: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      padding: "10px 14px",
      cursor: "pointer",
      fontSize: "14px",
      color: "#4a5568",
      minWidth: "auto",
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      whiteSpace: "nowrap",
      transition: "all 0.2s ease",
    },
    dateInputMobile: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      backgroundColor: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: "6px",
      padding: "8px 12px",
      cursor: "pointer",
      fontSize: "13px",
      color: "#4a5568",
      minWidth: "auto",
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      whiteSpace: "nowrap",
      transition: "all 0.2s ease",
    },
    calendarDropdown: {
      position: "absolute",
      top: "calc(100% + 5px)",
      right: 0,
      width: isMobile ? "calc(100vw - 30px)" : "200px",
      backgroundColor: "#fff",
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
      borderRadius: "8px",
      zIndex: 1000,
      overflow: "hidden",
      border: "1px solid #edf2f7",
      maxHeight: "300px",
      overflowY: "auto",
    },
    dropdownItem: (isActive) => ({
      padding: isMobile ? "14px 16px" : "12px 20px",
      cursor: "pointer",
      backgroundColor: isActive ? "#ff5e14" : "transparent",
      color: isActive ? "#fff" : "#4a5568",
      fontSize: isMobile ? "13px" : "14px",
      transition: "all 0.2s ease",
      borderBottom: "1px solid #f7fafc",
      userSelect: "none",
    }),
    banner: {
      background: "linear-gradient(90deg, #ff5e14 0%, #ff8c00 100%)",
      borderRadius: "15px",
      padding: isMobile ? "30px 20px" : "45px",
      color: "#fff",
      position: "relative",
      overflow: "hidden",
      marginBottom: "35px",
      boxShadow: "0 8px 30px rgba(255, 94, 20, 0.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      minHeight: isMobile ? "auto" : "300px",
    },
    diamondWrapper: {
      position: "absolute",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: 1,
    },
    bannerContent: {
      position: "relative",
      zIndex: 2,
      width: "100%",
      padding: isMobile ? "10px" : "0",
    },
    bannerOrb: {
      position: "absolute",
      width: "180px",
      height: "180px",
      borderRadius: "50%",
      background: "rgba(255,255,255,0.15)",
      bottom: "-60px",
      right: "5%",
      filter: "blur(40px)",
    },
    bannerButtons: {
      display: "flex",
      gap: "12px",
      position: "absolute",
      right: "40px",
      bottom: "40px",
      zIndex: 3,
    },
    btnDark: {
      backgroundColor: "#1a202c",
      border: "none",
      color: "white",
      padding: "10px 24px",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: "600",
    },
    btnLight: {
      backgroundColor: "#fff",
      border: "none",
      color: "#1a202c",
      padding: "10px 24px",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: "600",
    },
    statCard: {
      border: "none",
      borderRadius: "16px",
      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
      padding: "24px",
      marginBottom: "24px",
      backgroundColor: "#fff",
    },
    iconBox: {
      backgroundColor: "#1a202c",
      color: "white",
      width: "44px",
      height: "44px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "10px",
      marginBottom: "20px",
      fontSize: "18px",
    },
    badge: (type) => ({
      padding: "5px 10px",
      borderRadius: "6px",
      fontSize: "12px",
      fontWeight: "700",
      backgroundColor: type === "up" ? "#10b981" : "#ef4444",
      color: "white",
    }),
  };

  const TinyChart = ({ color, heights }) => (
    <svg width="90" height="60" viewBox="0 0 90 60">
      {heights.map((h, i) => (
        <rect
          key={i}
          x={i * 12}
          y={60 - h}
          width="8"
          height={h}
          fill={color}
          rx="2"
        />
      ))}
    </svg>
  );

  return (
    <div style={styles.wrapper}>
      {/* Header */}
      <div style={isMobile ? styles.headerRowMobile : styles.headerRow}>
      
 <Col md={4} sm={12}>
                  <div className="my-auto mb-2">
                    <h4 style={{ fontWeight: "600", marginBottom: "5px" }}>  Dashboard</h4>
        
                    <Breadcrumb
                      style={{
                        background: "transparent",
                        padding: 0,
                        margin: 0,
                        fontSize: "14px",
                      }}
                    >
                      <Breadcrumb.Item>
                      <Link  to="/" >
               
                        <RiHome6Line
                          className="m text-dark"
                          style={{
                            fontSize: "1.2rem", // increased icon size
                            verticalAlign: "middle",
                                                     }}
                        /></Link>
                      </Breadcrumb.Item>
        
                      <MdKeyboardArrowRight className="mt-1 fs-5" />
        
                      <Breadcrumb.Item
                        linkAs="span"
                        className="text-dark text-decoration-none fs-6"
                      >
                      Super Admin
                      </Breadcrumb.Item>
        
                      <MdKeyboardArrowRight className="mt-1 fs-5" />
        
                      <Breadcrumb.Item active>
                        <span className="text-dark fs-6">Dashboard</span>
                      </Breadcrumb.Item>
                    </Breadcrumb>
                  </div>
                </Col>
        <div style={styles.datePickerContainer} ref={dropdownRef}>
          <div
            style={isMobile ? styles.dateInputMobile : styles.dateInput}
            onClick={() => setShowDatePicker(!showDatePicker)}
          >
            <CalendarViewDay style={{ fontSize: "18px", flexShrink: 0 }} />
            <span style={{ whiteSpace: "nowrap" }}>{selectedRange}</span>
          </div>

          {showDatePicker && (
            <div style={styles.calendarDropdown}>
              {dateOptions.map((opt) => (
                <div
                  key={opt}
                  style={styles.dropdownItem(activeOption === opt)}
                  onClick={() => handleDateSelect(opt)}
                >
                  {opt}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Welcome Banner */}
      <>
        <style>
          {`
        .welcome-wrapper {
          position: relative;
          background: linear-gradient(90deg,#ff6a2f,#ff5722);
          border-radius: 12px;
          padding: 45px 50px;
          overflow: hidden;
        }

        .welcome-title {
          color: #fff;
          font-size: 30px;
          font-weight: 700;
        }

        .welcome-sub {
          color: #ffe6dd;
          font-size: 16px;
          margin-top: 6px;
        }

        /* Diamond Position (Exact Center Like Image) */
        .diamond-icon {
          position: absolute;
          top: 50%;
          left: 55%;
          transform: translate(-50%, -50%);
          // width: 65px;
        }

        /* Top Left Glow */
        .bg-circle-top {
          position: absolute;
          top: -40px;
          left: -40px;
          width: 140px;
          opacity: 0.4;
        }

        /* Bottom Right Large Circle */
        .bg-circle-bottom {
          position: absolute;
          right: -60px;
          bottom: -60px;
          width: 260px;
          opacity: 0.5;
        }

        @media (max-width: 992px) {
          .diamond-icon {
            left: 50%;
            top: 60%;
          //   width: 50px;
          }
        }

        @media (max-width: 768px) {
          .welcome-wrapper {
            padding: 10px 25px;
          }

          .welcome-title {
            font-size: 22px;
          }

           .diamond-icon {
        bottom: -40px;
          }
        }
        `}
        </style>

        <Container fluid className="mb-4">
          <div className="welcome-wrapper">
            <Row className="align-items-center">
              <Col lg={7} md={12} className="mb-3">
                <h2 className="welcome-title">Welcome Back, Adrian</h2>
                <p className="welcome-sub">
                  14 New Companies Subscribed Today !!!
                </p>
              </Col>

              <Col
                lg={5}
                md={12}
                className="text-lg-end text-start mt-3 mt-lg-0"
              >
                <Link to="/companies-button">
                  <Button
                    variant="dark"
                    className="me-2 mb-2 px-4"
                    style={{ borderRadius: "6px", fontWeight: "500" }}
                  >
                    Companies
                  </Button>
                </Link>

                <Button
                  variant="light"
                  className="mb-2 px-4"
                  style={{ borderRadius: "6px", fontWeight: "500" }}
                >
                  All Packages
                </Button>
              </Col>
            </Row>

            {/* Background Shapes */}
            <img
              src="/assets/img/bg/welcome-bg-02.svg"
              alt="bg"
              className="bg-circle-top"
            />

            <img
              src="/assets/img/bg/welcome-bg-01.svg"
              alt="bg"
              className="bg-circle-bottom"
            />

            {/* Diamond Icon (Exact Placement) */}
            <img
              src="/assets/img/bg/welcome-bg-03.svg"
              alt="diamond"
              className="diamond-icon"
            />
          </div>
        </Container>
      </>

      {/* Stats Grid */}
      <Container fluid style={{ padding: "20px" }}>
        <Row className="g-4">
          {/* Total Companies */}
          <Col xl={3} sm={6} xs={12}>
            <Card
              style={{
                borderRadius: "12px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                border: "1px solid #eee",
              }}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div
                    style={{
                      backgroundColor: "#1e1e2d",
                      padding: "12px",
                      borderRadius: "8px",
                      color: "#fff",
                    }}
                  >
                    <BsBuilding size={18} />
                  </div>
                  <Badge bg="success">+19.01%</Badge>
                </div>

                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h3 style={{ fontWeight: "700", marginBottom: "4px" }}>
                      5468
                    </h3>
                    <p style={{ fontSize: "14px", color: "#6c757d" }}>
                      Total Companies
                    </p>
                  </div>

                  <div style={barStyle}>
                    {renderBars([5, 10, 7, 5, 10, 7, 5], "#ff6b00")}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Active Companies */}
          <Col xl={3} sm={6} xs={12}>
            <Card
              style={{
                borderRadius: "12px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                border: "1px solid #eee",
              }}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div
                    style={{
                      backgroundColor: "#1e1e2d",
                      padding: "12px",
                      borderRadius: "8px",
                      color: "#fff",
                    }}
                  >
                    <BsBarChartSteps size={18} />
                  </div>
                  <Badge bg="danger">-12%</Badge>
                </div>

                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h3 style={{ fontWeight: "700", marginBottom: "4px" }}>
                      4598
                    </h3>
                    <p style={{ fontSize: "14px", color: "#6c757d" }}>
                      Active Companies
                    </p>
                  </div>

                  <div style={barStyle}>
                    {renderBars([5, 3, 7, 6, 3, 10, 5], "#6f42c1")}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Total Subscribers */}
          <Col xl={3} sm={6} xs={12}>
            <Card
              style={{
                borderRadius: "12px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                border: "1px solid #eee",
              }}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div
                    style={{
                      backgroundColor: "#1e1e2d",
                      padding: "12px",
                      borderRadius: "8px",
                      color: "#fff",
                    }}
                  >
                    <BsPersonWorkspace size={18} />
                  </div>
                  <Badge bg="success">+6%</Badge>
                </div>

                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h3 style={{ fontWeight: "700", marginBottom: "4px" }}>
                      3698
                    </h3>
                    <p style={{ fontSize: "14px", color: "#6c757d" }}>
                      Total Subscribers
                    </p>
                  </div>

                  <div style={barStyle}>
                    {renderBars([8, 10, 10, 8, 8, 10, 8], "#0d6efd")}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Total Earnings */}
          <Col xl={3} sm={6} xs={12}>
            <Card
              style={{
                borderRadius: "12px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                border: "1px solid #eee",
              }}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div
                    style={{
                      backgroundColor: "#1e1e2d",
                      padding: "12px",
                      borderRadius: "8px",
                      color: "#fff",
                    }}
                  >
                    <BsCurrencyDollar size={18} />
                  </div>
                  <Badge bg="danger">-16%</Badge>
                </div>

                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h3 style={{ fontWeight: "700", marginBottom: "4px" }}>
                      $89,878.58
                    </h3>
                    <p style={{ fontSize: "14px", color: "#6c757d" }}>
                      Total Earnings
                    </p>
                  </div>

                  <div style={barStyle}>
                    {renderBars([5, 10, 7, 5, 10, 7, 5], "#20c997")}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Settings Side Tab */}
      <div
        style={{
          position: "fixed",
          right: 0,
          top: "50%",
          backgroundColor: "#ff5e14",
          color: "white",
          padding: "12px",
          borderTopLeftRadius: "10px",
          borderBottomLeftRadius: "10px",
          cursor: "pointer",
          boxShadow: "-4px 0 15px rgba(0,0,0,0.1)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={`${process.env.PUBLIC_URL}/assets/img/icons/action-icon-3.svg`}
          alt="settings"
          style={{ width: 20, height: 20 }}
        />
      </div>
    </div>
  );
};

export default Superdashboard;
